import type { ErrorStatus } from '@highimpact/dashboard-ui';
import { controlPanelSelectorBoxSx } from '@highimpact/source-selector';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { SxProps, Theme } from '@mui/material/styles';
import type { FormEventHandler, ReactNode } from 'react';

/** Shared width constraint used by source selectors and time-range sections. */
export const plotConfigFormSectionWidthSx = {
  width: 300,
  maxWidth: '100%',
} as const;

/** Divider placed between source selection and time-range blocks. */
export const plotConfigFormDividerSx: SxProps<Theme> = {
  my: 1.25,
  ...plotConfigFormSectionWidthSx,
};

export function PlotConfigFormShell({
  onSubmit,
  children,
  sx,
}: {
  onSubmit: FormEventHandler;
  children: ReactNode;
  sx?: SxProps<Theme>;
}) {
  return (
    <Box component="form" onSubmit={onSubmit} sx={{ p: 2, ...sx }} noValidate>
      {children}
    </Box>
  );
}

export function PlotConfigSection({
  title,
  children,
  sx,
}: {
  title?: string;
  children: ReactNode;
  sx?: SxProps<Theme>;
}) {
  return (
    <Box sx={{ ...plotConfigFormSectionWidthSx, ...sx }}>
      {title ? (
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
          {title}
        </Typography>
      ) : null}
      <Box sx={controlPanelSelectorBoxSx}>{children}</Box>
    </Box>
  );
}

export function PlotConfigFormDivider() {
  return <Divider sx={plotConfigFormDividerSx} />;
}

export function PlotConfigInvalidStatus({ error }: { error: ErrorStatus }) {
  if (error.tag === 'Not submitted' || error.errors.length === 0) {
    return null;
  }

  return (
    <Box mt={2} sx={plotConfigFormSectionWidthSx}>
      <Typography color="error">
        {`Sources de données invalides: ${error.errors.join(', ')}`}
      </Typography>
    </Box>
  );
}

export function PlotConfigFieldErrors({
  errors,
  submitted,
}: {
  errors: readonly string[];
  submitted: boolean;
}) {
  if (!submitted || errors.length === 0) {
    return null;
  }

  return (
    <Typography color="error" variant="body2" sx={{ mt: 1 }}>
      {errors.join('. ')}
    </Typography>
  );
}

export function PlotConfigNameField({
  label = 'Nom',
  value,
  onChange,
  errors = [],
  submitted = false,
  required = false,
}: {
  label?: string;
  value: string | undefined;
  onChange: (value: string) => void;
  errors?: readonly string[];
  submitted?: boolean;
  required?: boolean;
}) {
  const hasError = submitted && errors.length > 0;

  return (
    <PlotConfigSection title={label}>
      <TextField
        size="small"
        fullWidth
        required={required}
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value)}
        error={hasError}
        label={label}
      />
      <PlotConfigFieldErrors errors={errors} submitted={submitted} />
    </PlotConfigSection>
  );
}

export function PlotConfigSubmitButton({
  isSending,
  loading = false,
  disabled = false,
}: {
  isSending: boolean;
  loading?: boolean;
  disabled?: boolean;
}) {
  return (
    <Button
      style={{ marginTop: '10px' }}
      type="submit"
      variant="contained"
      color="primary"
      disabled={isSending || loading || disabled}
      startIcon={isSending ? <CircularProgress size={20} /> : null}
    >
      Valider
    </Button>
  );
}
